import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Card, Form, Row, Col, Button, Table, Alert, Badge } from 'react-bootstrap';
import { addProjectAction } from '../../store/reducers/project';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import Select from 'react-select';
import DatePicker from '@app/components/CustomDatePicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Get } from '../../utils/httpProvider';
import { ANNUAL_JOB_API } from '../../utils/apiUrl';
import { EXTRAORDINARY_JOB_GROUP_NAME } from '../../utils/define';

const ProjectAdd = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { listAccount } = useSelector((state) => state.account);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [catalogJobs, setCatalogJobs] = useState([]);
    const [selectedAnnualJobId, setSelectedAnnualJobId] = useState('');
    // { [userId]: percent }
    const [memberPercents, setMemberPercents] = useState({});

    useEffect(() => {
        dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        Get(ANNUAL_JOB_API.GET_ALL_ACTIVE, null, null)
            .then((res) => { if (res) setCatalogJobs(res); })
            .catch((err) => console.error('Lỗi tải danh mục công việc', err));
    }, [dispatch]);

    // Filter catalogJobs theo Nhóm công việc "Nhiệm vụ đột xuất" (tên nhóm cấu hình)
    const extraordinaryJobs = catalogJobs.filter(j => {
        const groupName = (j.jobGroupName || j.JobGroupName || '').trim().toLowerCase();
        const target = (EXTRAORDINARY_JOB_GROUP_NAME || '').trim().toLowerCase();
        return groupName === target;
    });
    const availableJobs = extraordinaryJobs.length > 0 ? extraordinaryJobs : catalogJobs;

    const selectedJob = catalogJobs.find(jg => String(jg.id) === String(selectedAnnualJobId));
    // Điểm lấy theo điểm của chính Danh mục công việc (weight) thay vì của Nhóm công việc
    const jobGroupWeight = selectedJob ? Number(selectedJob.weight ?? selectedJob.Weight ?? selectedJob.jobGroupWeight ?? 0) : 0;

    const memberOptions = listAccount
        .filter(item => {
            const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
            const roles = item.roles || item.Roles || acc?.roles || acc?.Roles || [];
            return roles.some(r => (r.name || r.Name) === 'APP_KPI_C9');
        })
        .map(item => {
            const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
            const id = item.accountId || item.AccountId || item.id || item.Id || acc?.id || acc?.Id;
            const username = item.username || item.Username || acc?.username || acc?.Username;
            const authInfo = item.accountAuthInfo || item.AccountAuthInfo || item;
            const lastName = authInfo.lastName || '';
            const middleName = authInfo.middleName || '';
            const firstName = authInfo.firstName || '';
            const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
            return { value: id, label: fullName ? `${fullName} (${username})` : username || id };
        });

    const selectedMemberIds = Object.keys(memberPercents);

    const handleMembersChange = useCallback((selected) => {
        const newIds = selected ? selected.map(x => x.value) : [];
        setMemberPercents(prev => {
            const next = {};
            if (newIds.length === 0) return next;
            const perPerson = newIds.length > 0 ? (100 / newIds.length) : 0;
            newIds.forEach(uid => {
                // giữ lại weight cũ nếu đã có, không thì phân đều
                next[uid] = prev[uid] !== undefined ? prev[uid] : perPerson;
            });
            return next;
        });
    }, [jobGroupWeight]);

    const handleJobChange = (selectedOption) => {
        const newJobId = selectedOption ? selectedOption.value : '';
        setSelectedAnnualJobId(newJobId);
    };

    const totalPercent = Object.values(memberPercents).reduce((s, p) => s + Number(p || 0), 0);
    const totalWeight = (jobGroupWeight * totalPercent) / 100;

    const handlePercentChange = (uid, val) => {
        setMemberPercents(prev => ({ ...prev, [uid]: val === '' ? '' : Number(val) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error('Vui lòng nhập tên công việc'); return; }
        const hasInvalidPercent = Object.values(memberPercents).some(p => Number(p || 0) < 0);
        if (hasInvalidPercent) {
            toast.error('Tỉ lệ của từng thành viên không được âm');
            return;
        }
        const members = Object.entries(memberPercents).map(([uid, p]) => ({
            userId: uid,
            weight: (jobGroupWeight * Number(p || 0)) / 100
        }));

        const model = {
            name: name.trim(),
            description: description.trim(),
            status: 0,
            startDate: startDate || null,
            endDate: endDate || null,
            memberIds: selectedMemberIds,
            members,
            annualJobId: selectedAnnualJobId ? Number(selectedAnnualJobId) : null
        };

        try {
            setSubmitting(true);
            await dispatch(addProjectAction(model)).unwrap();
            toast.success('Tạo dự án thành công');
            history.push('/project');
        } catch (err) {
            toast.error('Tạo dự án thất bại');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <Card.Header as="h5">Tạo dự án mới</Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    {/* Tên công việc */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Tên công việc <span className="text-danger">*</span></Form.Label>
                        <Col sm={10}>
                            <Form.Control
                                type="text"
                                placeholder="Nhập tên công việc"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Col>
                    </Form.Group>

                    {/* Danh mục công việc */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Danh mục công việc</Form.Label>
                        <Col sm={10}>
                            <Select
                                isClearable
                                placeholder="-- Chọn danh mục công việc --"
                                value={selectedAnnualJobId ? {
                                    value: selectedAnnualJobId,
                                    label: (() => {
                                        const job = catalogJobs.find(j => String(j.id) === String(selectedAnnualJobId));
                                        return job ? `${job.name} - Điểm: ${job.weight ?? job.Weight ?? job.jobGroupWeight ?? 0}` : '';
                                    })()
                                } : null}
                                onChange={handleJobChange}
                                options={availableJobs.map((job) => ({
                                    value: job.id,
                                    label: `${job.name} - Điểm: ${job.weight ?? job.Weight ?? job.jobGroupWeight ?? 0}`
                                }))}
                            />
                        </Col>
                    </Form.Group>

                    {/* Ngày */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Ngày bắt đầu</Form.Label>
                        <Col sm={4}>
                            <DatePicker
                                className="form-control"
                                selected={startDate ? new Date(startDate) : null}
                                onChange={(date) => setStartDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="dd/MM/yyyy"
                                isClearable
                            />
                        </Col>
                        <Form.Label column sm={2}>Ngày kết thúc dự kiến</Form.Label>
                        <Col sm={4}>
                            <DatePicker
                                className="form-control"
                                selected={endDate ? new Date(endDate) : null}
                                onChange={(date) => setEndDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="dd/MM/yyyy"
                                isClearable
                            />
                        </Col>
                    </Form.Group>

                    {/* Mô tả */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Mô tả</Form.Label>
                        <Col sm={10}>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    {/* Người tham gia */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Người tham gia</Form.Label>
                        <Col sm={10}>
                            <Select
                                isMulti
                                options={memberOptions}
                                placeholder="Gõ tên để tìm kiếm và chọn thành viên..."
                                value={memberOptions.filter(opt => selectedMemberIds.includes(opt.value))}
                                onChange={handleMembersChange}
                                classNamePrefix="select"
                                isSearchable={true}
                                isClearable={true}
                                noOptionsMessage={() => 'Không tìm thấy thành viên nào có quyền APP_KPI_C9'}
                            />
                        </Col>
                    </Form.Group>

                    {/* Bảng trọng số thành viên */}
                    {selectedMemberIds.length > 0 && (
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm={2}>
                                Trọng số thành viên
                                {selectedJob && (
                                    <div className="mt-1">
                                        <Badge variant="info" style={{ fontSize: '0.75rem' }}>
                                            Nhóm: {jobGroupWeight}
                                        </Badge>
                                    </div>
                                )}
                            </Form.Label>
                            <Col sm={10}>
                                {!selectedJob && (
                                    <Alert variant="warning" className="py-2 mb-2" style={{ fontSize: 13 }}>
                                        Chọn danh mục công việc để tự động tính trọng số
                                    </Alert>
                                )}
                                <Table bordered size="sm" style={{ fontSize: 13 }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Thành viên</th>
                                            <th className="text-center" style={{ width: 100 }}>Tỉ lệ (%)</th>
                                            <th style={{ width: 160 }}>
                                                Điểm
                                                {selectedJob && (
                                                    <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                                                        (Tổng: {totalWeight.toFixed(2)}/{jobGroupWeight})
                                                    </span>
                                                )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedMemberIds.map(uid => {
                                            const opt = memberOptions.find(o => String(o.value) === String(uid));
                                            return (
                                                <tr key={uid}>
                                                    <td className="align-middle">{opt?.label || uid}</td>
                                                    <td className="align-middle text-center">
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={0.01}
                                                            value={memberPercents[uid] ?? 0}
                                                            onChange={(e) => handlePercentChange(uid, e.target.value)}
                                                            size="sm"
                                                            style={{ width: 120, margin: '0 auto' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle fw-bold">
                                                        {((jobGroupWeight * (memberPercents[uid] ?? 0)) / 100).toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Col>
                        </Form.Group>
                    )}

                    {/* Buttons */}
                    <Form.Group as={Row}>
                        <Col sm={{ span: 10, offset: 2 }}>
                            <div className="d-flex gap-2">
                                <Button type="submit" variant="primary" disabled={submitting}>
                                    {submitting ? 'Đang lưu...' : 'Tạo dự án'}
                                </Button>
                                <Button variant="secondary" onClick={() => history.push('/project')} disabled={submitting}>
                                    Hủy
                                </Button>
                            </div>
                        </Col>
                    </Form.Group>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default ProjectAdd;
